import { useState } from "react";

import type { SharedRebateProgramPartnerIbStatus } from "../../api/generated";

import { BottomSheet } from "../../BottomSheet";
import { Button as ButtonV2 } from "../../Button";
import { StatusPill } from "../../StatusPill";
import { TooltipWrapper } from "../../Tooltip";
import { Typo } from "../../Typo";
import { useSubIbStatusTagTranslations } from "./useSubIbStatusTagTranslations";

type SubIbStatusTagProps = {
  status: SharedRebateProgramPartnerIbStatus | undefined;
  isMobile?: boolean;
  hasRejectedNotification?: boolean;
  onRejectedNotificationSeen?: () => void;
};

const Pill = ({
  status,
  label,
  showDot = false,
}: {
  status: "pendingKYC" | "rejected";
  label: string;
  showDot?: boolean;
}) =>
  status === "rejected" ? (
    <span className="relative inline-flex pr-1">
      <StatusPill status="Rejected" label={label} />
      {showDot && (
        <span
          className="absolute right-0 top-0 h-2 w-2 rounded-full bg-surface-brand-default"
          aria-hidden="true"
        />
      )}
    </span>
  ) : (
    <StatusPill status="Pending KYC" label={label} />
  );

export const SubIbStatusTag = ({
  status,
  isMobile,
  hasRejectedNotification = false,
  onRejectedNotificationSeen,
}: SubIbStatusTagProps) => {
  const translations = useSubIbStatusTagTranslations();
  const [open, setOpen] = useState(false);

  if (status !== "pendingKYC" && status !== "rejected") return null;

  const label =
    status === "rejected"
      ? translations.rejectedLabel
      : translations.pendingLabel;
  const message =
    status === "rejected"
      ? translations.rejectedMessage
      : translations.pendingMessage;

  if (!isMobile) {
    return (
      <TooltipWrapper
        text={message}
        color="blue"
        tooltipPlacement="top"
        tooltipMaxWidth={240}
      >
        <span
          onMouseEnter={() => {
            if (status === "rejected" && hasRejectedNotification) {
              onRejectedNotificationSeen?.();
            }
          }}
        >
          <Pill
            status={status}
            label={label}
            showDot={hasRejectedNotification}
          />
        </span>
      </TooltipWrapper>
    );
  }

  const closeSheet = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        data-testid="rebate_programs_sub_ib_status_tag_button"
        className="inline-flex shrink-0"
        onClick={(e) => {
          e.stopPropagation();
          if (status === "rejected" && hasRejectedNotification) {
            onRejectedNotificationSeen?.();
          }
          setOpen(true);
        }}
      >
        <Pill status={status} label={label} showDot={hasRejectedNotification} />
      </button>
      <BottomSheet
        isOpen={open}
        onClose={closeSheet}
        title={label}
        isDraggable
        contentContainerClassName="py-3"
        footer={
          <ButtonV2
            data-testid="rebate_programs_sub_ib_status_sheet_got_it_button"
            variant="brand"
            size="md"
            className="w-full"
            onClick={closeSheet}
          >
            {translations.gotItLabel}
          </ButtonV2>
        }
      >
        <Typo element="p" type="bodySM" textColor="text-text-primary">
          {message}
        </Typo>
      </BottomSheet>
    </>
  );
};
